import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from './api-client';
import { getErrorMessage } from './errors';
import type {
  Album,
  FileMetadata,
  RedeemOTPRequest,
  RedeemOTPResponse,
  ShareRequest,
  ShareResponse,
  AlbumListResponse,
  UploadInitRequest,
  UploadCompleteResponse,
  ViewLinkResponse,
  MemoirListResponse,
  AccessEntry,
  AccessLogResponse,
} from "../../../../types"

/**
 * Generic async operation state
 */
interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to access the API client.
 *
 * No wiring left to do: the client pulls tokens straight from the auth
 * module (`getAccessToken()`), which refreshes them on demand, so there is
 * no provider instance to register the way the previous auth SDK required.
 */
export function useApiClient() {
  return apiClient;
}

/**
 * Hook for sharing albums (generating OTPs)
 */
export function useShareAlbum() {
  const [state, setState] = useState<AsyncState<ShareResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const shareAlbum = useCallback(async (uuid: string, request: ShareRequest) => {
    setState({ data: null, loading: true, error: null });
    
    try {
      const response = await apiClient.shareAlbum(uuid, request);
      setState({ data: response, loading: false, error: null });
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setState({ data: null, loading: false, error: errorMessage });
      throw error;
    }
  }, []);

  return {
    ...state,
    shareAlbum,
  };
}

/**
 * Hook for OTP redemption
 */
export function useRedeemOTP() {
  const [state, setState] = useState<AsyncState<RedeemOTPResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const redeemOTP = useCallback(async (request: RedeemOTPRequest) => {
    setState({ data: null, loading: true, error: null });
    
    try {
      const response = await apiClient.redeemOTP(request);
      setState({ data: response, loading: false, error: null });
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setState({ data: null, loading: false, error: errorMessage });
      throw error;
    }
  }, []);

  return {
    ...state,
    redeemOTP,
  };
}

/**
 * Hook for fetching user's albums
 */
/**
 * The signed-in user's Memoirs (SOL-204), for the Memoirs home and for scoping
 * the album workspace. `refresh` is what the post-purchase page polls: the
 * Memoir appears only once Stripe's webhook has seen the payment settle.
 */
export function useMemoirs() {
  const [state, setState] = useState<AsyncState<MemoirListResponse>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchMemoirs = useCallback(async (silent = false) => {
    if (!silent) setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.getMemoirs();
      setState({ data: response, loading: false, error: null });
      return response;
    } catch (error) {
      setState((prev) => ({ data: prev.data, loading: false, error: getErrorMessage(error) }));
      return null;
    }
  }, []);

  useEffect(() => {
    void fetchMemoirs();
  }, [fetchMemoirs]);

  return {
    memoirs: state.data?.memoirs ?? [],
    invitations: state.data?.invitations ?? [],
    loading: state.loading,
    error: state.error,
    refresh: fetchMemoirs,
  };
}

const MAX_EMPTY_FOLLOWS = 5;

/**
 * A Memoir's access log (SOL-203): the first page on mount, then `loadMore`
 * follows the server's cursor and appends. A failed "load more" keeps what is
 * already on screen and reports the error.
 */
export function useAccessLog(memoirId: string | undefined) {
  const [entries, setEntries] = useState<AccessEntry[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [viewer, setViewer] = useState<'owner' | 'client' | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A page can come back empty WITH a cursor (the server scans a bounded
  // number of keys per call and a client's entries may sit further down). Keep
  // following it, a bounded number of times, before showing "Load more".
  const follow = useCallback(async (first: AccessLogResponse, id: string) => {
    let page = first;
    let all = first.entries;
    for (let i = 0; i < MAX_EMPTY_FOLLOWS && page.entries.length === 0 && page.cursor; i++) {
      page = await apiClient.getAccessLog(id, page.cursor);
      all = all.concat(page.entries);
    }
    return { page, all };
  }, []);

  const load = useCallback(async () => {
    if (!memoirId) return;
    setLoading(true);
    setError(null);
    try {
      const { page, all } = await follow(await apiClient.getAccessLog(memoirId), memoirId);
      setEntries(all);
      setCursor(page.cursor);
      setViewer(page.viewer);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [memoirId, follow]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = useCallback(async () => {
    if (!memoirId || !cursor) return;
    setLoadingMore(true);
    setError(null);
    try {
      const { page, all } = await follow(await apiClient.getAccessLog(memoirId, cursor), memoirId);
      setEntries((prev) => [...prev, ...all]);
      setCursor(page.cursor);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoadingMore(false);
    }
  }, [memoirId, cursor, follow]);

  return { entries, hasMore: !!cursor, viewer, loading, loadingMore, error, loadMore, refresh: load };
}

export function useAlbums() {
  const [state, setState] = useState<AsyncState<AlbumListResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchAlbums = useCallback(async () => {
    setState({ data: null, loading: true, error: null });
    
    try {
      const response = await apiClient.getAlbums();
      setState({ data: response, loading: false, error: null });
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setState({ data: null, loading: false, error: errorMessage });
      throw error;
    }
  }, []);

  const refresh = useCallback(() => fetchAlbums(), [fetchAlbums]);

  useEffect(() => {
    fetchAlbums();
  }, [fetchAlbums]);

  return {
    albums: state.data?.albums || [],
    loading: state.loading,
    error: state.error,
    refresh,
    fetchAlbums,
  };
}

/**
 * Hook for fetching a specific album
 */
export function useAlbum(uuid: string | null) {
  const [state, setState] = useState<AsyncState<{ album: Album; files: FileMetadata[] }>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchAlbum = useCallback(async (albumUuid: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await apiClient.getAlbum(albumUuid);
      setState({ data: response, loading: false, error: null });
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setState({ data: null, loading: false, error: errorMessage });
      throw error;
    }
  }, []);

  useEffect(() => {
    if (uuid) {
      fetchAlbum(uuid);
    }
  }, [uuid, fetchAlbum]);

  const refresh = useCallback(() => {
    if (uuid) {
      fetchAlbum(uuid);
    }
  }, [uuid, fetchAlbum]);

  return {
    album: state.data?.album || null,
    files: state.data?.files || [],
    loading: state.loading,
    error: state.error,
    refresh,
    fetchAlbum,
  };
}

/**
 * Hook for file downloads
 */
export function useFileDownload() {
  const [state, setState] = useState<AsyncState<Blob>>({
    data: null,
    loading: false,
    error: null,
  });

  const downloadFile = useCallback(async (albumUuid: string, fileName: string) => {
    setState({ data: null, loading: true, error: null });
    
    try {
      const blob = await apiClient.downloadFile(albumUuid, fileName);
      setState({ data: blob, loading: false, error: null });
      
      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      return blob;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setState({ data: null, loading: false, error: errorMessage });
      throw error;
    }
  }, []);

  return {
    ...state,
    downloadFile,
  };
}

/**
 * Individual file upload status
 */
export interface FileUploadStatus {
  name: string;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  previewUrl?: string;
}

/**
 * Upload progress state for multiple files
 */
export interface UploadProgress {
  totalFiles: number;
  completedFiles: number;
  currentFile: string;
  currentFileProgress: number;
  overallProgress: number;
  status: 'idle' | 'initializing' | 'uploading' | 'completing' | 'done' | 'error';
  error?: string;
  // Per-file progress for UI placeholders
  files: FileUploadStatus[];
}

/**
 * Thrown by `uploadFiles` when a run is already in flight. Callers can
 * check `instanceof UploadInProgressError` to distinguish "please wait,
 * nothing started" from an actual upload failure.
 */
export class UploadInProgressError extends Error {
  constructor() {
    super('An upload is already in progress. Please wait for it to finish.');
    this.name = 'UploadInProgressError';
  }
}

const initialUploadProgress: UploadProgress = {
  totalFiles: 0,
  completedFiles: 0,
  currentFile: '',
  currentFileProgress: 0,
  overallProgress: 0,
  status: 'idle',
  files: [],
};

/**
 * Module-scoped (not per-hook-instance) mutex: true while any
 * `usePresignedUpload()` consumer's `uploadFiles()` call is in flight.
 *
 * This has to live outside the hook. Multiple components on the same
 * screen each call `usePresignedUpload()` independently and get their own
 * state - e.g. the empty-state onboarding view renders both
 * `FolderToolbar`'s `UploadInterface` and `AlbumList`'s own empty-album
 * dropzone at once, each backed by a separate hook instance. A per-instance
 * `useRef` mutex only stops a second drop on the *same* widget; it does
 * nothing to stop the user from dropping onto the toolbar panel and then
 * the big dropzone below, each with no `albumId`, creating two albums. One
 * upload at a time per browser tab is the correct product semantic here
 * (and what a user already assumes "upload" means), so the lock is shared
 * at module scope - every instance in this tab sees the same flag.
 */
let isUploadInFlight = false;

/**
 * Hook for uploading files with presigned URLs (direct to R2)
 *
 * Guarantees enforced here (not left to callers, so every caller gets them
 * for free):
 * - Only one upload runs at a time across the whole tab, not just per
 *   widget; a second call while one is in flight throws
 *   `UploadInProgressError` instead of silently starting a second
 *   album-creating run (see SOL-117 follow-up).
 * - `resetProgress` (and unmount) invalidate any still-running upload's
 *   state updates via a per-instance generation counter, so a run that
 *   outlives the UI that started it can't resurrect stale progress after
 *   the fact. The network calls it already issued still complete in the
 *   background - this is not cancellation, just corruption-proofing. Real
 *   cancellation would need an AbortController plumbed through
 *   api-client's fetch/XHR calls.
 * - Object URLs created for image previews are revoked when the run that
 *   created them fails (the moment they become dead, not on the next run -
 *   a later run's previews may still be genuinely on screen), in
 *   `resetProgress`, and when the hook unmounts, so previews can't leak.
 */
export function usePresignedUpload() {
  const [progress, setProgress] = useState<UploadProgress>(initialUploadProgress);

  // Mirrors `progress` synchronously for use inside callbacks/cleanup that
  // must read the latest files list without taking a stale closure over
  // `progress` or forcing `uploadFiles`/`resetProgress` to be recreated
  // every time progress changes.
  const progressRef = useRef(progress);
  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  // Bumped on every new run and on every reset, so in-flight setProgress
  // calls from a superseded/reset run can detect they're stale and no-op.
  // Deliberately per-instance (unlike the mutex above): this only ever
  // needs to protect this hook instance's own `progress` state from its
  // own stale runs.
  const runGenerationRef = useRef(0);

  // Revoke previews left over from a previous run before the hook goes away.
  useEffect(() => {
    return () => {
      runGenerationRef.current++;
      progressRef.current.files.forEach(f => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
  }, []);

  const resetProgress = useCallback(() => {
    // Invalidate any run still writing to this state and revoke whatever
    // previews it (or a prior finished/errored run) left behind.
    runGenerationRef.current++;
    progressRef.current.files.forEach(f => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setProgress(initialUploadProgress);
  }, []);

  const uploadFiles = useCallback(async (
    albumName: string,
    files: File[],
    // memoirId: the Memoir a NEW album is created in (SOL-204). The server
    // refuses a new album without one (MEMOIR_REQUIRED); an existing album
    // already carries its own, so `albumId` alone is enough there.
    options?: { albumId?: string; parentId?: string; memoirId?: string }
  ): Promise<UploadCompleteResponse> => {
    if (isUploadInFlight) {
      // Visible refusal, not a silent no-op: without this, a second drop -
      // even from a different upload widget on the same screen - would fire
      // a second uploadFiles() with no albumId and create a second album
      // for what the user meant as one action.
      throw new UploadInProgressError();
    }
    isUploadInFlight = true;
    const runId = ++runGenerationRef.current;

    // Apply a progress update only if this run hasn't been superseded or
    // reset since it started.
    const applyProgress = (updater: (prev: UploadProgress) => UploadProgress) => {
      if (runGenerationRef.current !== runId) return;
      setProgress(updater);
    };

    // Declared here (not `const` inside try) so the catch block can still
    // see - and revoke - whatever previews did get created if something
    // later in the try throws. Assigned inside try, below.
    let initialFileStatus: FileUploadStatus[] = [];

    try {
      const totalFiles = files.length;

      // Create preview URLs and initial file status. URL.createObjectURL
      // can throw (e.g. blob-registry exhaustion - ironically the exact
      // class of problem this file's revoke logic exists to prevent), so
      // this now happens inside try/finally: a throw here still reaches
      // the finally below and releases the mutex, instead of leaving
      // isUploadInFlight stuck true - which, since the mutex is
      // module-scoped, would wedge every usePresignedUpload() consumer in
      // the tab until a full reload.
      initialFileStatus = files.map(f => ({
        name: f.name,
        progress: 0,
        status: 'pending' as const,
        previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
      }));

      // Step 1: Initialize upload and get presigned URLs
      applyProgress(() => ({
        totalFiles,
        completedFiles: 0,
        currentFile: '',
        currentFileProgress: 0,
        overallProgress: 0,
        status: 'initializing',
        files: initialFileStatus,
      }));

      const initRequest: UploadInitRequest = {
        albumName,
        albumId: options?.albumId,
        parentId: options?.parentId,
        memoirId: options?.memoirId,
        files: files.map(f => ({
          name: f.name,
          size: f.size,
          type: f.type || 'application/octet-stream',
        })),
      };

      const initResponse = await apiClient.uploadInit(initRequest);

      // Step 2: Upload each file to R2
      applyProgress(prev => ({ ...prev, status: 'uploading' }));

      const uploadedFiles: { key: string; size: number }[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const urlInfo = initResponse.uploadUrls[i];

        // Mark current file as uploading
        applyProgress(prev => ({
          ...prev,
          currentFile: file.name,
          currentFileProgress: 0,
          files: prev.files.map((f, idx) =>
            idx === i ? { ...f, status: 'uploading' as const } : f
          ),
        }));

        await apiClient.uploadFileToR2(
          urlInfo.presignedUrl,
          file,
          (fileProgress) => {
            const overallProgress = ((i + fileProgress / 100) / totalFiles) * 100;
            applyProgress(prev => ({
              ...prev,
              currentFileProgress: fileProgress,
              overallProgress,
              files: prev.files.map((f, idx) =>
                idx === i ? { ...f, progress: fileProgress } : f
              ),
            }));
          },
          urlInfo.sse
        );

        uploadedFiles.push({
          key: urlInfo.key,
          size: file.size,
        });

        // Mark file as done
        applyProgress(prev => ({
          ...prev,
          completedFiles: i + 1,
          overallProgress: ((i + 1) / totalFiles) * 100,
          files: prev.files.map((f, idx) =>
            idx === i ? { ...f, status: 'done' as const, progress: 100 } : f
          ),
        }));
      }

      // Step 3: Complete upload
      applyProgress(prev => ({ ...prev, status: 'completing' }));

      const completeResponse = await apiClient.uploadComplete({
        albumId: initResponse.albumId,
        // Names this batch's pending record (SOL-209), so another batch into
        // the same album, from another tab or device, cannot displace it.
        uploadId: initResponse.uploadId,
        uploadedFiles,
      });

      applyProgress(prev => ({
        ...prev,
        status: 'done',
        overallProgress: 100,
      }));

      return completeResponse;
    } catch (error) {
      // This run failed as a whole - uploadComplete() never ran, so even
      // files that individually made it to R2 before the failure are not
      // part of any album and never will be for this run. Their previews'
      // job is over the moment we know that, so revoke them here rather
      // than waiting for a future run or resetProgress() to do it - the
      // caller may retry with a fresh file picker without ever calling
      // resetProgress() (isBusy excludes 'error', so the file input
      // remounts immediately), which is exactly the leak this closes.
      //
      // Deliberately revoking initialFileStatus (this run's own preview
      // URLs) rather than progressRef.current.files: a later, unrelated
      // run's previews may be genuinely on screen (e.g. "Add More" into
      // the same album while this state is mid-transition) and must not
      // be touched here.
      initialFileStatus.forEach(f => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });

      const errorMessage = getErrorMessage(error);
      applyProgress(prev => ({
        ...prev,
        status: 'error',
        error: errorMessage,
      }));
      throw error;
    } finally {
      // The run's network calls have settled either way - release the
      // mutex so a new upload can start, regardless of whether this run's
      // state updates were still being applied.
      isUploadInFlight = false;
    }
  }, []);

  return {
    progress,
    uploadFiles,
    resetProgress,
  };
}

/**
 * Hook for managing a view link for an album
 */
export function useViewLink(albumUuid: string) {
  const [link, setLink] = useState<ViewLinkResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // `link === null` alone cannot tell "this album has no link" (a 404, which
  // getViewLink maps to null) apart from "we could not find out" (a 500, a
  // timeout, offline). Callers must not offer creation in the second case:
  // POST is a rotation, so one click would destroy a live URL the owner was
  // never shown.
  const [loadFailed, setLoadFailed] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoadFailed(false);
    try {
      setLink(await apiClient.getViewLink(albumUuid));
    } catch (err) {
      setError(getErrorMessage(err));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [albumUuid]);

  useEffect(() => { void refresh(); }, [refresh]);

  const create = useCallback(async (expiresInDays: number | null) => {
    setError(null);
    try {
      const created = await apiClient.createViewLink(albumUuid, { expiresInDays });
      setLink(created);
      setLoadFailed(false);
      return created;
    } catch (err) {
      setError(getErrorMessage(err));
      throw err;
    }
  }, [albumUuid]);

  const revoke = useCallback(async () => {
    setError(null);
    try {
      await apiClient.revokeViewLink(albumUuid);
      setLink(null);
      setLoadFailed(false);
    } catch (err) {
      setError(getErrorMessage(err));
      throw err;
    }
  }, [albumUuid]);

  return { link, loading, error, loadFailed, refresh, create, revoke };
}
